import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import RadioGroup from 'components/ui/RadioGroup/RadioGroup';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { formatPriceProposal } from 'utils/shared/utils';
import { showWarning } from 'utils/utils';
import { MainContext } from 'contexts/MainContext';

interface EditPriceModalProps {
  openData?: { value: string; type: 'p' | 'v' | 'e' };
  onClose: (type?: 'p' | 'v' | 'e', value?: number, isAll?: boolean) => void;
  isOpen: boolean;
  initialProduct?: {
    product: any;
    rcUniqueKey?: string;
  };
}

const EditPriceModal: FC<EditPriceModalProps> = ({ openData, onClose, isOpen, ...props }) => {
  const { t } = useTranslation();
  const [priceType, setPriceType] = useState<'p' | 'v' | 'e'>('v');
  const [priceValue, setPriceValue] = useState('');
  const { discountHotelLimit } = useContext(MainContext);
  const currency = sessionStorage.getItem('displayCurrencyHotels') || 'usd';
  const minPrice = useRef(0);
  const initialPrice = useRef(0);

  useEffect(() => {
    if (openData?.type && openData?.value && isOpen) {
      setPriceType(openData.type);
      setPriceValue(openData.value);
    } else {
      setPriceType('v');
      setPriceValue('');
    }

    if (props.initialProduct?.product?.item?.roomClasses && props.initialProduct.rcUniqueKey) {
      const rc = props.initialProduct.product.item.roomClasses.find(
        (item: any) => item.rcUniqueKey === props.initialProduct?.rcUniqueKey
      );

      minPrice.current = Math.round(rc?.price?.[currency] * (rc?.price?.llv || 1));
      initialPrice.current = rc?.price?.[currency] || 0;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleUpdate = () => {
    if (discountHotelLimit && minPrice.current) {
      const resultPrice = formatPriceProposal(
        initialPrice.current,
        currency,
        priceType as any,
        +priceValue
      );

      const resultPriceNumber = parseFloat(resultPrice.replace(/[^0-9.]/g, ''));

      if (resultPriceNumber < minPrice.current) {
        showWarning(t('proposal.discount_hotel_limit', { minPrice: minPrice.current }));
        return;
      }
    }
    if (priceValue) {
      onClose(priceType, +priceValue, false);
    }
  };

  const handleUpdateAll = () => {
    if (priceValue) {
      onClose(priceType, +priceValue, true);
    }
  };
  const onChangeValue = (value: string) => {
    setPriceType(value as 'p' | 'v' | 'e');

    if (value === 'e' && props.initialProduct) {
      let price = 0;
      if (props.initialProduct?.rcUniqueKey && props.initialProduct?.product.item.roomClasses) {
        const rc = props.initialProduct.product.item.roomClasses.find(
          (item: any) => item.rcUniqueKey === props.initialProduct?.rcUniqueKey
        );
        price = rc?.price?.[currency] || 0;
      } else {
        price = props.initialProduct.product.price;
      }
      setPriceValue(Math.round(price).toString());
    } else {
      setPriceValue('');
    }
  };

  if (!openData) return null;

  return createPortal(
    <div className="modal-overlay modal-overlay-top">
      <div className="modal active modal-sm">
        <div className="modal-title">
          <div className="title">{t('proposal.edit_price')}</div>
          <div>
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content">
          <div className="flex-column">
            <RadioGroup
              options={[
                { value: 'v', label: t('proposal.fixed_value') },
                { value: 'p', label: t('proposal.percentage') },
                { value: 'e', label: t('proposal.overwrite_price') },
              ]}
              value={priceType}
              onChange={onChangeValue}
              className="mb-20"
            />
            <FormGroup
              label={t('proposal.value')}
              value={priceValue}
              onChange={setPriceValue}
              allowOnlyNumbers={true}
              validation={{ required: true }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <Button
            className="btn btn-primary ml-0"
            title={t('proposal.update')}
            onClick={handleUpdate}
          />
          <Button
            className="btn btn-primary mr-a"
            title={t('proposal.update_all')}
            onClick={handleUpdateAll}
            disabled={priceType === 'e'}
          />
          <Button className="btn btn-default" title={t('close')} onClick={() => onClose()} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditPriceModal;
